/**
 * Smart Packing · /dashboard/packing
 *
 * Phase 1 — functional core:
 *   · Setup form (10 profile answers on top of the case-auto-populated 5)
 *   · Personalised checklist with 7 tabs
 *   · 4 readiness cards
 *   · Filters (search + priority + incomplete + category + bag-location)
 *   · Item cards with status controls, quantity, note
 *   · Restricted items require explicit non-'packed' verification status
 *
 * Preserves existing design language: cream base, black type, thin ink/12 borders,
 * rounded cards, blue accent, yellow for urgent, green for complete.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Package, CheckCircle2, Circle, AlertTriangle, ShieldCheck, ShieldAlert,
  ShoppingBag, MapPin, ExternalLink, Search, Filter, PenLine, Info,
  Plane, Home as HomeIcon, Sparkles, ArrowLeft,
} from "lucide-react";

// -------------------------------------------------------------------- setup
const YES_NO = [{ label: "Yes", v: true }, { label: "No", v: false }];

function Setup({ data, onSave }) {
  // iter76 (Item D) — Cross-product data pre-fill: derive departure_date from
  // the case's start_date + duration_days so we never ask the user for what
  // the case already knows. Users can still override the derived date in the
  // input if their exchange dates changed. Never fabricated when the case is
  // missing either field — falls back to empty and requires manual entry.
  const _derivedDeparture = (() => {
    const s = data.case_start_date;
    const d = data.case_duration_days;
    if (!s || !d) return "";
    try {
      const [y, m, dd] = s.split("-").map(Number);
      const start = new Date(Date.UTC(y, m - 1, dd));
      start.setUTCDate(start.getUTCDate() + Number(d));
      return start.toISOString().slice(0, 10);
    } catch { return ""; }
  })();
  const [f, setF] = useState({
    arrival_date:                   data.profile?.arrival_date || data.case_start_date || "",
    departure_date:                 data.profile?.departure_date || _derivedDeparture || "",
    airline:                        data.profile?.airline || "",
    cabin_allowance_kg:             data.profile?.cabin_allowance_kg || "",
    cabin_dimensions:               data.profile?.cabin_dimensions || "",
    checked_allowance_kg:           data.profile?.checked_allowance_kg || "",
    checked_bag_count:              data.profile?.checked_bag_count || 1,
    accommodation_type:             data.profile?.accommodation_type || "",
    bedding_included:               data.profile?.bedding_included ?? null,
    towels_included:                data.profile?.towels_included ?? null,
    kitchen_available:              data.profile?.kitchen_available ?? null,
    utensils_provided:              data.profile?.utensils_provided ?? null,
    cooks_regularly:                data.profile?.cooks_regularly ?? null,
    dietary_preference:             data.profile?.dietary_preference || "",
    carries_prescription_medicine:  data.profile?.carries_prescription_medicine ?? false,
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...f };
      // numeric coercion
      if (payload.cabin_allowance_kg   === "") payload.cabin_allowance_kg   = null;
      if (payload.checked_allowance_kg === "") payload.checked_allowance_kg = null;
      const r = await api.post("/packing/profile", payload);
      if (r.data.profile_complete) { toast.success("Packing profile saved."); onSave(); }
      else                          toast.error("Please answer every question first.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const set = (k) => (v) => setF({ ...f, [k]: v });
  const inputCls = "mt-1 w-full px-3 py-2 border border-ink/20 rounded-lg text-sm focus:outline-none focus:border-elec bg-white";

  // iter73 — Small pink asterisk indicator used next to required-field
  // labels. Standard form UX so users know at a glance which questions must
  // be answered to save the setup. `aria-hidden` because the "required"
  // semantics on the underlying input already communicate this to assistive
  // tech; the asterisk is purely visual reinforcement.
  const Req = () => (
    <span aria-hidden="true" className="text-[color:var(--moe-pink)] font-bold ml-0.5" data-testid="req-star">*</span>
  );

  const YNRow = ({ label, k, help }) => (
    <div className="py-3 border-b border-ink/10">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm font-bold text-ink">{label} <Req /></div>
          {help && <div className="text-xs text-muted mt-0.5">{help}</div>}
        </div>
        <div className="flex gap-1 shrink-0">
          {YES_NO.map((y) => (
            <button
              type="button"
              key={String(y.v)}
              data-testid={`setup-${k}-${y.v ? "yes" : "no"}`}
              onClick={() => set(k)(y.v)}
              className={"px-3 py-1 rounded-full text-xs font-bold border transition-colors " +
                (f[k] === y.v ? "bg-ink text-white border-ink" : "text-ink border-ink/20 hover:border-ink")}
            >
              {y.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <form onSubmit={submit} className="moe-card p-6 md:p-8 max-w-3xl" data-testid="packing-setup">
      <div className="text-[11px] uppercase tracking-widest text-elec font-bold">Set up your Smart Packing</div>
      <h2 className="mt-2 text-2xl md:text-3xl font-bold tracking-tight leading-tight">
        Six questions. Then a personalised checklist.
      </h2>
      <p className="mt-2 text-sm text-muted max-w-xl">
        Destination is <b>{data.profile?.destination_country || data.case_country || "your host country"}</b>, city <b>{data.profile?.destination_city || data.case_host_city || "your host city"}</b>. Pulled from your case. Fill the rest below.
      </p>

      {/* iter73 — Small legend so the asterisk convention is discoverable
         before the user scrolls into the form. */}
      <div className="mt-3 text-[11px] text-muted" data-testid="setup-required-legend">
        Fields marked <span className="text-[color:var(--moe-pink)] font-bold">*</span> are required.
      </div>

      {/* Dates */}
      <div className="mt-8 grid md:grid-cols-2 gap-5">
        <div>
          <label className="text-xs uppercase tracking-widest text-soft font-bold">Arrival date <Req /></label>
          <input data-testid="setup-arrival" type="date" required value={f.arrival_date} onChange={(e) => set("arrival_date")(e.target.value)} className={inputCls}/>
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-soft font-bold">Departure date <Req /></label>
          <input data-testid="setup-departure" type="date" required value={f.departure_date} onChange={(e) => set("departure_date")(e.target.value)} className={inputCls}/>
        </div>
      </div>

      {/* Airline */}
      <div className="mt-6 grid md:grid-cols-2 gap-5">
        <div>
          <label className="text-xs uppercase tracking-widest text-soft font-bold">Airline (as booked)</label>
          <input data-testid="setup-airline" value={f.airline} onChange={(e) => set("airline")(e.target.value)} placeholder="e.g. Lufthansa" className={inputCls}/>
          <div className="text-[11px] text-muted mt-1">We never guess baggage allowances. Confirm yours from the ticket.</div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs uppercase tracking-widest text-soft font-bold">Cabin bag (kg)</label>
            <input data-testid="setup-cabin-kg" type="number" step="0.5" min="0" value={f.cabin_allowance_kg} onChange={(e) => set("cabin_allowance_kg")(e.target.value === "" ? "" : Number(e.target.value))} className={inputCls}/>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-soft font-bold">Checked bag (kg)</label>
            <input data-testid="setup-checked-kg" type="number" step="0.5" min="0" value={f.checked_allowance_kg} onChange={(e) => set("checked_allowance_kg")(e.target.value === "" ? "" : Number(e.target.value))} className={inputCls}/>
          </div>
        </div>
      </div>

      {/* Accommodation */}
      <div className="mt-6">
        <label className="text-xs uppercase tracking-widest text-soft font-bold">Accommodation type <Req /></label>
        <select data-testid="setup-accom" required value={f.accommodation_type} onChange={(e) => set("accommodation_type")(e.target.value)} className={inputCls}>
          <option value="">Select one</option>
          <option>Student residence</option>
          <option>Shared apartment / WG</option>
          <option>Private apartment</option>
          <option>Host family / homestay</option>
          <option>Not yet confirmed</option>
        </select>
      </div>

      <div className="mt-2">
        <YNRow label="Is bedding provided?"                    k="bedding_included"  help="Sheets, pillow, duvet. If yes, we won't have you carry linens."/>
        <YNRow label="Are towels provided?"                    k="towels_included"   help="If not, packing a compact quick-dry towel makes sense."/>
        <YNRow label="Is a kitchen available in your housing?" k="kitchen_available" help="Determines whether we suggest cooking essentials."/>
        <YNRow label="Are kitchen utensils/pots provided?"     k="utensils_provided" help="Only relevant if kitchen is available."/>
        <YNRow label="Will you cook regularly?"                k="cooks_regularly"   help="Compact cooking items appear only for cooks."/>
        <YNRow label="Do you carry prescription medicine?"     k="carries_prescription_medicine" help="Reminds you to pack the doctor's note + labels."/>
      </div>

      {/* Dietary */}
      <div className="mt-4">
        <label className="text-xs uppercase tracking-widest text-soft font-bold">Dietary preference</label>
        <select data-testid="setup-diet" value={f.dietary_preference} onChange={(e) => set("dietary_preference")(e.target.value)} className={inputCls}>
          <option value="">No preference</option>
          <option>Vegetarian</option>
          <option>Vegan</option>
          <option>Halal</option>
          <option>Kosher</option>
          <option>Jain</option>
        </select>
      </div>

      <button data-testid="setup-submit" disabled={busy} type="submit" className="mt-8 moe-btn moe-btn-primary">
        {busy ? "Saving…" : "Generate my checklist"}
      </button>
    </form>
  );
}


// ---------------------------------------------------------------- helpers
const PRIORITY_CLASS = {
  "Essential":   "bg-elec/10 text-elec",
  "Required":    "bg-elec/10 text-elec",
  "Recommended": "bg-ink/8 text-ink",
  "Optional":    "bg-ink/5 text-muted",
  "Conditional": "bg-[color:var(--moe-yolk)]/40 text-ink",
  "Buy locally": "bg-[color:var(--moe-yolk)]/40 text-ink",
  "Verify before packing": "bg-[color:var(--moe-pink)]/25 text-[color:var(--moe-pink)]",
  "Avoid":       "bg-[color:var(--moe-pink)]/25 text-[color:var(--moe-pink)]",
};

const STATUS_DONE = new Set(["packed", "skipped", "buy_after_arrival",
                             "added_to_shopping_list", "purchased", "needs_verification"]);


// -------------------------------------------------------------------- item card
function isOfficialSource(url) {
  if (!url) return false;
  try {
    const host = new URL(url).hostname.toLowerCase();
    if (/(^|\.)europa\.eu$/.test(host)) return true;
    if (/\.gov(\.[a-z]{2,3})?$/.test(host)) return true;
    if (/(^|\.)bmi\.gv\.at$/.test(host)) return true;
    if (/(^|\.)admin\.ch$/.test(host)) return true;
    if (/(^|\.)aa\.dk$/.test(host)) return true;
    if (/(^|\.)migri\.fi$/.test(host)) return true;
    if (/(^|\.)nyidanmark\.dk$/.test(host)) return true;
    if (/(^|\.)dgca\./.test(host)) return true;
    if (/(^|\.)caa\./.test(host)) return true;
    return false;
  } catch { return false; }
}

function ItemCard({ item, onStatus }) {
  const [note, setNote] = useState(item.user_note || "");
  const [qty, setQty]   = useState(item.user_quantity || "");
  const [editing, setEditing] = useState(false);
  const isRestricted = item.record_type === "restricted_item";
  const isBuyAfter   = item.record_type === "buy_after_arrival";
  const done = STATUS_DONE.has(item.status);
  const sourceIsGov = isOfficialSource(item.source_url);

  const setStatus = async (status) => {
    try {
      await api.post("/packing/state", { record_id: item.record_id, status,
        user_quantity: qty || null, user_note: note || null });
      onStatus(item.record_id, status);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  return (
    <div className={"rounded-2xl border p-4 md:p-5 transition-colors " +
      (done ? "border-elec/40 bg-elec/[0.04]" : "border-ink/12 bg-white hover:border-ink/25")}
      data-testid={`item-${item.record_id}`}
    >
      <div className="flex items-start gap-3">
        {/* Checkbox. Restricted items get a shield instead */}
        {isRestricted ? (
          <div className="mt-0.5 shrink-0">
            {done ? <ShieldCheck size={18} className="text-elec"/> : <ShieldAlert size={18} className="text-[color:var(--moe-pink)]"/>}
          </div>
        ) : (
          <button
            onClick={() => setStatus(done ? "not_started" : "packed")}
            data-testid={`item-check-${item.record_id}`}
            className="mt-0.5 shrink-0"
            aria-label="Toggle packed"
          >
            {done ? <CheckCircle2 size={20} className="text-elec"/> : <Circle size={20} className="text-ink/40 hover:text-ink"/>}
          </button>
        )}

        <div className="min-w-0 flex-1">
          {/* Row 1. Name + badges */}
          <div className="flex flex-wrap items-center gap-1.5">
            <div className={"font-bold text-sm " + (done ? "line-through text-muted" : "text-ink")}>
              {item.item_name}
            </div>
            {item.suggested_quantity && (
              <span className="text-[11px] text-soft font-bold">· {item.suggested_quantity}</span>
            )}
            <span className={"text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded " + (PRIORITY_CLASS[item.priority] || "bg-ink/5 text-muted")}>
              {item.priority}
            </span>
            {item.bag_location && (
              <span className="text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded bg-ink/5 text-muted">
                {item.bag_location}
              </span>
            )}
            {isBuyAfter && <span className="text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded bg-[color:var(--moe-yolk)]/40 text-ink">Buy after arrival</span>}
            {item.airline_confirmation_required && <span className="text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded bg-[color:var(--moe-pink)]/20 text-[color:var(--moe-pink)] inline-flex items-center gap-0.5"><Plane size={10}/> Airline confirm</span>}
          </div>

          {/* Row 2. Why + country note */}
          {item.why_needed && <div className="mt-2 text-xs text-muted leading-relaxed">{item.why_needed}</div>}
          {item.country_specific_note && (
            <div className="mt-2 rounded-lg bg-tint/60 border border-ink/8 p-2.5 text-xs text-ink">
              <MapPin size={12} className="inline mr-1 -mt-0.5"/>{item.country_specific_note}
            </div>
          )}
          {item.customs_or_safety_warning && (
            <div className="mt-2 rounded-lg bg-[color:var(--moe-pink)]/10 border border-[color:var(--moe-pink)]/25 p-2.5 text-xs text-ink">
              <AlertTriangle size={12} className="inline mr-1 -mt-0.5 text-[color:var(--moe-pink)]"/>{item.customs_or_safety_warning}
            </div>
          )}
          {item.source_url && (
            <a
              href={item.source_url}
              target="_blank"
              rel="noreferrer"
              className="mt-2 inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-widest text-elec hover:underline"
              data-testid={`item-source-${item.record_id}`}
            >
              {sourceIsGov ? "Official guidance" : "Reference"} <ExternalLink size={11}/>
            </a>
          )}

          {/* Row 3. Restricted-item explicit verification actions */}
          {isRestricted && (
            <div className="mt-3 flex flex-wrap gap-1.5" data-testid={`item-restricted-actions-${item.record_id}`}>
              <button onClick={() => setStatus("skipped")}
                      className={"text-[11px] font-bold px-2 py-1 rounded-full border " +
                        (item.status === "skipped" ? "bg-ink text-white border-ink" : "border-ink/25 text-ink hover:border-ink")}>
                Not carrying
              </button>
              <button onClick={() => setStatus("needs_verification")}
                      className={"text-[11px] font-bold px-2 py-1 rounded-full border " +
                        (item.status === "needs_verification" ? "bg-ink text-white border-ink" : "border-ink/25 text-ink hover:border-ink")}>
                Verified with airline / customs
              </button>
            </div>
          )}

          {/* Row 4. Buy-after-arrival actions */}
          {isBuyAfter && !isRestricted && (
            <div className="mt-3 flex flex-wrap gap-1.5" data-testid={`item-buy-actions-${item.record_id}`}>
              <button onClick={() => setStatus("added_to_shopping_list")}
                      className={"text-[11px] font-bold px-2 py-1 rounded-full border " +
                        (item.status === "added_to_shopping_list" ? "bg-ink text-white border-ink" : "border-ink/25 text-ink hover:border-ink")}>
                Add to arrival shopping list
              </button>
              <button onClick={() => setStatus("purchased")}
                      className={"text-[11px] font-bold px-2 py-1 rounded-full border " +
                        (item.status === "purchased" ? "bg-ink text-white border-ink" : "border-ink/25 text-ink hover:border-ink")}>
                Purchased
              </button>
              <button onClick={() => setStatus("skipped")}
                      className="text-[11px] font-bold px-2 py-1 rounded-full border border-ink/20 text-muted hover:border-ink">
                Skip
              </button>
            </div>
          )}

          {/* Edit qty + note */}
          {editing && (
            <div className="mt-3 grid md:grid-cols-2 gap-2" data-testid={`item-edit-${item.record_id}`}>
              <input value={qty} onChange={(e) => setQty(e.target.value)} placeholder="Your quantity" className="px-2.5 py-1.5 text-xs border border-ink/20 rounded-lg" data-testid={`item-qty-${item.record_id}`}/>
              <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note to self" className="px-2.5 py-1.5 text-xs border border-ink/20 rounded-lg" data-testid={`item-note-${item.record_id}`}/>
            </div>
          )}

          {!isRestricted && (
            <div className="mt-3 flex gap-3 text-[11px] font-bold text-muted">
              <button onClick={() => setEditing((v) => !v)} className="hover:text-ink inline-flex items-center gap-1">
                <PenLine size={11}/> {editing ? "Save" : "Add qty / note"}
              </button>
              {!isBuyAfter && (
                <button onClick={() => setStatus("skipped")} className="hover:text-ink" data-testid={`item-skip-${item.record_id}`}>
                  Skip
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


// -------------------------------------------------------------------- main
const TABS = [
  { k: "overview",   label: "Overview" },
  { k: "cabin",      label: "Cabin Bag" },
  { k: "checked",    label: "Checked Luggage" },
  { k: "country",    label: "Country & Season" },
  { k: "buy",        label: "Buy After Arrival" },
  { k: "restricted", label: "Restricted" },
  { k: "final",      label: "Final 48 Hours" },
];

export default function Packing() {
  const [state, setState]   = useState(null);
  const [summary, setSum]   = useState(null);
  const [tab, setTab]       = useState("overview");
  const [q, setQ]           = useState("");
  const [prio, setPrio]     = useState("all");
  const [onlyOpen, setOnlyOpen] = useState(false);
  const [custom, setCustom]  = useState([]);
  const [newCustom, setNewCustom] = useState("");

  const load = () => Promise.all([
    api.get("/packing/checklist").then((r) => setState(r.data)),
    api.get("/packing/summary").then((r) => setSum(r.data)),
    api.get("/packing/custom-items").then((r) => setCustom(r.data.items || [])).catch(() => setCustom([])),
  ]);
  useEffect(() => { load(); }, []);

  // iter70 — custom-item add/toggle/delete. Freeform, per case, no priority.
  const addCustom = async () => {
    const name = newCustom.trim();
    if (!name) return;
    await api.post("/packing/custom-items", { item_name: name });
    setNewCustom("");
    const r = await api.get("/packing/custom-items");
    setCustom(r.data.items || []);
  };
  const toggleCustom = async (id) => {
    await api.post(`/packing/custom-items/${id}/toggle`);
    const r = await api.get("/packing/custom-items");
    setCustom(r.data.items || []);
  };
  const deleteCustom = async (id) => {
    await api.delete(`/packing/custom-items/${id}`);
    setCustom((cs) => cs.filter((c) => c.id !== id));
  };

  const items = state?.items || [];
  const filtered = useMemo(() => {
    let xs = items;
    const bytab = {
      overview:   () => xs,
      cabin:      () => xs.filter((i) => /cabin|personal item/i.test(i.bag_location || "")),
      checked:    () => xs.filter((i) => /checked/i.test(i.bag_location || "")),
      country:    () => xs.filter((i) => i.record_type === "country_rule" || i.record_type === "seasonal_rule" || i.category === "Country Essentials"),
      buy:        () => xs.filter((i) => i.record_type === "buy_after_arrival"),
      restricted: () => xs.filter((i) => i.record_type === "restricted_item" || i.record_type === "avoid_item"),
      final:      () => xs.filter((i) => i.record_type === "final_check" || i.record_type === "pre_departure_task"),
    };
    xs = bytab[tab]();
    if (q)     xs = xs.filter((i) => (i.item_name + " " + (i.why_needed || "")).toLowerCase().includes(q.toLowerCase()));
    if (prio !== "all") xs = xs.filter((i) => i.priority === prio);
    if (onlyOpen) xs = xs.filter((i) => !STATUS_DONE.has(i.status));
    return xs;
  }, [items, tab, q, prio, onlyOpen]);

  if (!state) {
    return <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-10 pb-24 text-sm text-muted">Loading…</div>;
  }
  if (!state.profile_complete) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-10 pb-24" data-testid="packing-page">
        <Link to="/dashboard" className="text-xs text-muted hover:text-ink inline-flex items-center gap-1 mb-6"><ArrowLeft size={12}/> Back to dashboard</Link>
        <div className="text-xs uppercase tracking-widest text-elec font-bold">Smart Packing</div>
        <h1 className="mt-2 text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight">A packing list built for your exact trip.</h1>
        <div className="mt-8"><Setup data={state} onSave={load}/></div>
      </div>
    );
  }

  const p = state.profile;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-10 pb-24" data-testid="packing-page">
      <Link to="/dashboard" className="text-xs text-muted hover:text-ink inline-flex items-center gap-1"><ArrowLeft size={12}/> Back to dashboard</Link>
      <div className="mt-4 flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-elec font-bold">Smart Packing</div>
          <h1 className="mt-1 text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight">{p.destination_country} · {p.destination_city}</h1>
          <div className="mt-2 text-sm text-muted">
            {p.arrival_date} → {p.departure_date} · {p.total_days} days · <span className="text-ink">{state.seasons.filter((s) => s !== "ALL").join(" / ") || "All-season"}</span>
          </div>
        </div>
      </div>

      {/* Readiness cards */}
      {summary && (
        <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4" data-testid="packing-readiness-cards">
          <StatCard testid="stat-readiness"  label="Pack before departure" pct={summary.readiness_pct}      note={`${summary.counts.essential.done + summary.counts.recommended.done} / ${summary.counts.essential.total + summary.counts.recommended.total} items`}/>
          <StatCard testid="stat-cabin"      label="Cabin bag ready"       pct={summary.cabin_ready_pct}   icon={Plane}/>
          <StatCard testid="stat-arrival"    label="Buy after arrival"     pct={summary.arrival_shopping_pct} note={`${summary.counts.arrival.total} items`} icon={ShoppingBag}/>
          <StatCard testid="stat-compliance" label="Needs verification"    pct={summary.compliance_pct}     note={`${summary.restricted_unresolved} unresolved`} icon={ShieldAlert}/>
        </div>
      )}

      {/* Tabs */}
      <div className="mt-8 flex gap-1 border-b border-ink/10 overflow-x-auto">
        {TABS.map((t) => (
          <button
            key={t.k}
            data-testid={`packing-tab-${t.k}`}
            onClick={() => setTab(t.k)}
            className={"px-3 md:px-4 py-2.5 text-sm font-bold whitespace-nowrap border-b-2 -mb-px transition-colors " +
              (tab === t.k ? "border-elec text-ink" : "border-transparent text-muted hover:text-ink")}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted"/>
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="packing-search" placeholder="Search items…"
                 className="w-full pl-9 pr-3 py-2 text-sm border border-ink/20 rounded-lg focus:outline-none focus:border-elec bg-white"/>
        </div>
        <select value={prio} onChange={(e) => setPrio(e.target.value)} data-testid="packing-priority"
                className="px-3 py-2 text-sm border border-ink/20 rounded-lg bg-white">
          <option value="all">All priorities</option>
          <option>Essential</option>
          <option>Recommended</option>
          <option>Optional</option>
          <option>Buy locally</option>
          <option>Verify before packing</option>
          <option>Avoid</option>
        </select>
        <label className="inline-flex items-center gap-2 text-xs font-bold text-ink cursor-pointer">
          <input type="checkbox" checked={onlyOpen} onChange={(e) => setOnlyOpen(e.target.checked)} data-testid="packing-only-open"/>
          Incomplete only
        </label>
      </div>

      {/* iter70 · Your items. Freeform user-added checklist. Sits above
           the generated list so it's visible without a filter change. */}
      <div className="mt-6 rounded-xl border border-ink/10 bg-tint p-4" data-testid="packing-custom-section">
        <div className="text-xs uppercase tracking-widest font-bold text-ink mb-3">Your items · {custom.length}</div>
        <div className="flex gap-2 mb-3">
          <input
            value={newCustom}
            onChange={(e) => setNewCustom(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addCustom()}
            placeholder="Add something we didn't cover (e.g. Contact lens solution)"
            data-testid="packing-custom-input"
            className="flex-1 px-3 py-2 text-sm border border-ink/20 rounded-lg focus:outline-none focus:border-elec bg-white"
          />
          <button
            onClick={addCustom}
            disabled={!newCustom.trim()}
            data-testid="packing-custom-add"
            className="px-4 py-2 rounded-lg bg-ink text-white text-xs font-bold uppercase tracking-widest disabled:opacity-50 hover:bg-ink/90"
          >Add</button>
        </div>
        {custom.length === 0 ? (
          <div className="text-xs text-muted">Nothing custom yet. Add anything the checklist above doesn't already cover.</div>
        ) : (
          <ul className="space-y-1.5" data-testid="packing-custom-list">
            {custom.map((c) => (
              <li key={c.id} className="flex items-center gap-3 text-sm">
                <input
                  type="checkbox"
                  checked={c.status === "packed"}
                  onChange={() => toggleCustom(c.id)}
                  data-testid={`packing-custom-toggle-${c.id}`}
                  className="w-4 h-4 accent-elec cursor-pointer"
                />
                <span className={c.status === "packed" ? "line-through text-muted flex-1" : "text-ink flex-1"}>{c.item_name}</span>
                <button
                  onClick={() => deleteCustom(c.id)}
                  data-testid={`packing-custom-delete-${c.id}`}
                  aria-label={`Delete ${c.item_name}`}
                  className="text-xs text-muted hover:text-red-600 font-bold px-2"
                >×</button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Restricted-tab disclaimer */}
      {tab === "restricted" && (
        <div className="mt-6 rounded-2xl border border-[color:var(--moe-pink)]/25 bg-[color:var(--moe-pink)]/5 p-4 md:p-5 text-sm text-ink" data-testid="packing-restricted-disclaimer">
          <div className="font-bold text-[color:var(--moe-pink)] inline-flex items-center gap-1.5"><AlertTriangle size={14}/> Read this once</div>
          <div className="mt-1.5 text-xs md:text-sm text-ink leading-relaxed">
            Airline, airport-security and customs rules can differ. Always confirm unusual items with your airline and destination authority before packing. Opening a warning does not complete it. Mark each item explicitly as <b>Not carrying</b> or <b>Verified with airline / customs</b>.
          </div>
        </div>
      )}

      {/* Items */}
      <div className="mt-6 space-y-3" data-testid="packing-items">
        {filtered.length === 0 ? (
          <div className="text-sm text-muted py-10 text-center">Nothing matches these filters.</div>
        ) : filtered.map((it) => (
          <ItemCard key={it.record_id} item={it} onStatus={() => load()}/>
        ))}
      </div>
    </div>
  );
}

function StatCard({ testid, label, pct, note, icon: Icon }) {
  const tint = pct >= 80 ? "text-elec" : pct >= 40 ? "text-ink" : "text-muted";
  return (
    <div className="rounded-2xl border border-ink/12 p-4 bg-white" data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-soft font-bold">
        {Icon && <Icon size={12}/>} {label}
      </div>
      <div className={"mt-2 text-3xl font-black tracking-tight tabular-nums " + tint}>{pct}<span className="text-lg font-bold text-muted">%</span></div>
      {note && <div className="mt-1 text-[11px] text-muted">{note}</div>}
    </div>
  );
}
